/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from 'react'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { Avatar } from '@/shared/ui/Avatar'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { Check, Save, X } from 'lucide-react'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { useUsers, useRbacRoles, useRbacUserRoles, useSetRbacUserRoles } from '@/api/hooks'
import { toArray, idsOf, sameSet, type Role } from './rolesUtils'
import { SearchBox, ErrorNote, Center } from './rolesUi'

/** Give a user one or more roles: search the user by name/email, tick roles, save. */
export function UserRolesTab() {
  const confirm = useConfirm()

  // user search (debounced)
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => { const t = setTimeout(() => setDebounced(term.trim()), 300); return () => clearTimeout(t) }, [term])
  const searchQ = useUsers({ search: debounced, page: 1, limit: 8 }, { enabled: debounced.length >= 2 })
  const results: any[] = useMemo(() => toArray(searchQ.data, 'users'), [searchQ.data])

  const [user, setUser] = useState<any | null>(null)

  // roles
  const rolesQ = useRbacRoles()
  const roles: Role[] = useMemo(() => toArray(rolesQ.data, 'roles'), [rolesQ.data])
  const userRolesQ = useRbacUserRoles(user?.id)
  const setUserRoles = useSetRbacUserRoles()

  const saved = useMemo(() => new Set(idsOf(toArray(userRolesQ.data, 'roles'))), [userRolesQ.data])
  const [draft, setDraft] = useState<Set<number>>(new Set())
  useEffect(() => { setDraft(new Set(saved)) }, [saved])
  const dirty = !sameSet(draft, saved)

  const toggle = (id: number) => setDraft(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const pick = async (u: any) => {
    if (dirty && !(await confirm({ title: 'Discard unsaved changes?', description: `Role changes for ${user?.name ?? 'this user'} were not saved.`, confirmLabel: 'Discard', variant: 'danger' }))) return
    setUser(u); setTerm(''); setDebounced('')
  }

  const save = async () => {
    if (!user) return
    if (!(await confirm({
      title: 'Save roles?', description: `${user.name ?? 'This user'} will have ${draft.size} role${draft.size === 1 ? '' : 's'}. Their access changes immediately.`,
      confirmLabel: 'Save roles', variant: 'primary',
    }))) return
    try { await setUserRoles.mutateAsync({ userId: user.id, role_ids: [...draft] }); toast.success('User roles saved') }
    catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Card>
      <h3 className="font-bold text-gray-900">Assign roles to a user</h3>
      <p className="text-sm text-gray-500 mb-4">Find the person, then tick the roles they should have.</p>

      {/* 1 · find user */}
      <div className="relative max-w-md mb-5">
        <SearchBox value={term} onChange={setTerm} placeholder="Search by name, email or phone…" />
        {term.trim().length === 1 && <p className="text-xs text-gray-400 mt-1.5">Type at least 2 characters to search…</p>}
        {/\d/.test(term) && /^\d+$/.test(term.trim()) && (
          <button onClick={() => pick({ id: Number(term.trim()), name: `User #${term.trim()}` })}
            className="mt-2 text-xs font-medium text-brand-600 hover:underline">Use user ID {term.trim()} directly</button>
        )}
        {debounced.length >= 2 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
            {searchQ.isLoading ? <div className="py-4"><Center /></div> :
              searchQ.isError ? (
                <div className="p-3 text-center space-y-2">
                  <p className="text-sm text-red-600 whitespace-pre-line">Could not search users: {getApiError(searchQ.error)}</p>
                  <Button size="sm" variant="outline" onClick={() => searchQ.refetch()}>Retry</Button>
                </div>
              ) :
              results.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No users found for “{debounced}”</p> :
                results.map(u => (
                  <button key={u.id} onClick={() => pick(u)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50">
                    <Avatar name={u.name ?? 'U'} src={u.photo_url} size="sm" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{u.name ?? '—'}</span>
                      <span className="block text-xs text-gray-400 truncate">{u.email ?? u.phone ?? `#${u.id}`}</span>
                    </span>
                  </button>
                ))}
          </div>
        )}
      </div>

      {/* 2 · tick roles */}
      {!user ? (
        <p className="text-sm text-gray-400 py-8 text-center border border-dashed border-gray-200 rounded-xl">Search for a user above to manage their roles.</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={user.name ?? 'U'} src={user.photo_url} size="md" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user.name ?? `User #${user.id}`}</p>
                <p className="text-xs text-gray-500 truncate">{user.email ?? user.phone ?? `#${user.id}`}</p>
              </div>
            </div>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => pick(null)} aria-label="Clear user"><X size={16} /></button>
          </div>

          {rolesQ.isLoading || userRolesQ.isLoading ? <Center /> :
            rolesQ.isError ? <ErrorNote err={rolesQ.error} onRetry={() => rolesQ.refetch()} /> :
            userRolesQ.isError ? <ErrorNote err={userRolesQ.error} onRetry={() => userRolesQ.refetch()} /> :
            roles.length === 0 ? <p className="text-sm text-gray-400 py-6 text-center">No roles exist yet — create one in “Roles &amp; Access”.</p> : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                  {roles.map(r => {
                    const on = draft.has(r.id)
                    return (
                      <button key={r.id} onClick={() => toggle(r.id)}
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${on ? 'border-brand-300 bg-brand-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <span className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 ${on ? 'bg-brand-500 text-white' : 'bg-gray-200'}`}>{on && <Check size={11} />}</span>
                        <span className="min-w-0">
                          <span className={`block text-sm truncate ${on ? 'font-semibold text-brand-800' : 'font-medium text-gray-800'}`}>{r.name}</span>
                          {r.description && <span className="block text-xs text-gray-400 truncate">{r.description}</span>}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-3">
                  <Button icon={<Save size={13} />} disabled={!dirty} loading={setUserRoles.isPending} onClick={save}>Save roles</Button>
                  {dirty && <button className="text-sm text-gray-500 hover:text-gray-700" onClick={() => setDraft(new Set(saved))}>Discard changes</button>}
                  {!dirty && <span className="text-xs text-gray-400">{draft.size} role{draft.size === 1 ? '' : 's'} assigned</span>}
                </div>
              </>
            )}
        </>
      )}
    </Card>
  )
}
